import { useEffect, useState } from "react";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor, basisLabel, fmt, pct, signed } from "@/lib/utils-msip";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowLeft, Users, UploadCloud } from "lucide-react";
import { Input } from "@/components/ui/input";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monLabel = (m) => (m ? `${MON[+m.slice(5, 7) - 1]} ${m.slice(2, 4)}` : m);

const availTone = (a) => (a == null ? "text-muted-foreground" : a >= 95 ? "text-emerald-700" : a >= 80 ? "text-amber-700" : "text-red-600");

const CAT_BADGE = {
  sales: "bg-blue-50 text-blue-800",
  service: "bg-orange-50 text-orange-800",
  both: "bg-secondary text-muted-foreground",
};

function FillBar({ availability }) {
  const v = availability == null ? 0 : Math.min(availability, 100);
  const color = availability == null ? "#cbd5e1" : availability >= 95 ? "#0d765a" : availability >= 80 ? "#b45309" : "#dc2626";
  return (
    <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${v}%`, background: color }} />
    </div>
  );
}

function BranchCard({ name, onBack, canMarket }) {
  const { filters } = useApp();
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    api
      .get(`/hr/branches/${encodeURIComponent(name)}?${qs({ brand: filters.brand, basis: filters.basis })}`)
      .then(({ data }) => setD(data))
      .catch(() => setD(false));
  }, [name, filters.brand, filters.basis]);

  if (d === null) return <div className="text-sm text-muted-foreground py-20 text-center">Loading report card…</div>;
  if (d === false) return <div className="text-sm text-muted-foreground py-20 text-center">Could not load this branch.</div>;

  const mk = d.market;
  return (
    <div className="space-y-5" data-testid="branch-report-card">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs hover:bg-secondary transition-colors duration-200" data-testid="branch-back-btn">
          <ArrowLeft size={13} /> All branches
        </button>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">{d.display} <span className="text-muted-foreground font-medium text-base">· Report Card</span></h1>
        <span className="text-xs text-muted-foreground ml-auto">Manpower as of {d.as_of?.slice(0, 10)}{d.dealer_code ? ` · Dealer ${d.dealer_code}` : ""}{d.rto_codes.length > 0 ? ` · Market: ${d.rto_codes.join(", ")}` : ""}</span>
      </div>

      {mk && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border bg-card rounded-md p-4" data-testid="branch-kpi-mtd">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">MTD Share · {filters.brand}</div>
              <div className="num text-2xl font-bold mt-1">{pct(mk.mtd_share)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{fmt(mk.brand_units)} of {fmt(mk.tiv)} regs</div>
            </div>
            <div className="border bg-card rounded-md p-4" data-testid="branch-kpi-ytd">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">YTD Share</div>
              <div className="num text-2xl font-bold mt-1">{pct(mk.ytd_share)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{mk.ytd_label}</div>
            </div>
            <div className="border bg-card rounded-md p-4" data-testid="branch-kpi-rank">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Rank (MTD)</div>
              <div className="num text-2xl font-bold mt-1">{mk.rank ? `#${mk.rank}` : "—"}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{mk.mtd_label}</div>
            </div>
            <div className="border bg-card rounded-md p-4" data-testid="branch-kpi-rival">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Gap vs Nearest Rival</div>
              <div className={`num text-2xl font-bold mt-1 ${mk.gap_to_rival >= 0 ? "text-emerald-700" : "text-red-600"}`}>{signed(mk.gap_to_rival)} pts</div>
              <div className="text-xs text-muted-foreground mt-0.5">{mk.rival}: {pct(mk.rival_share)}</div>
            </div>
          </div>
          {mk.trend.length > 1 && (
            <div className="border bg-card rounded-md p-4" data-testid="branch-trend-chart">
              <h3 className="font-heading text-sm font-semibold mb-2">Monthly share trend — {filters.brand} ({basisLabel(filters.basis)})</h3>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={mk.trend} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={monLabel} />
                  <YAxis tick={{ fontSize: 11 }} unit="%" domain={["auto", "auto"]} />
                  <Tooltip formatter={(v) => `${v}%`} labelFormatter={monLabel} />
                  <Line type="monotone" dataKey="share" stroke={brandColor(filters.brand)} strokeWidth={2.5} dot={{ r: 3, fill: brandColor(filters.brand) }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
      {canMarket && !mk && (
        <div className="border bg-card rounded-md p-4 text-xs text-muted-foreground" data-testid="branch-no-market">
          No parent dealer linked to this branch yet — link one in Admin → Dealers to see market share here.
        </div>
      )}

      <div className="border bg-card rounded-md overflow-hidden" data-testid="branch-manpower">
        <div className="px-4 py-3 border-b flex flex-wrap items-center gap-x-6 gap-y-1">
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><Users size={14} /> Manpower</h3>
          <span className="text-xs">Required <b className="num">{d.totals.required}</b></span>
          <span className="text-xs">Current <b className="num">{d.totals.current}</b></span>
          <span className={`text-xs font-semibold ${d.totals.gap > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="branch-total-gap">Gap {d.totals.gap}</span>
          <span className={`text-xs font-semibold ${availTone(d.totals.availability)}`}>{d.totals.availability != null ? `${d.totals.availability}% filled` : ""}</span>
          <div className="w-40 ml-auto"><FillBar availability={d.totals.availability} /></div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b">
            <tr>{["Department / Designation", "Required", "Current", "Gap", "Avail %"].map((h, i) => (
              <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${i >= 1 ? "text-right" : "text-left"}`}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {d.departments.map((dep) => (
              [
                <tr key={dep.dept} className="bg-muted/40 border-b" data-testid={`dept-row-${dep.dept}`}>
                  <td className="px-3 py-1.5 font-semibold">
                    {dep.dept} <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${CAT_BADGE[dep.category]}`}>{dep.category}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right num font-semibold">{dep.required}</td>
                  <td className="px-3 py-1.5 text-right num font-semibold">{dep.current}</td>
                  <td className={`px-3 py-1.5 text-right num font-bold ${dep.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{dep.gap}</td>
                  <td className={`px-3 py-1.5 text-right num font-semibold ${availTone(dep.availability)}`}>{dep.availability != null ? `${dep.availability}%` : "—"}</td>
                </tr>,
                ...dep.rows.map((r, i) => (
                  <tr key={`${dep.dept}-${i}`} className={`border-b last:border-0 ${r.gap > 0 ? "bg-red-50/60" : ""}`}>
                    <td className="px-3 py-1 pl-7 text-muted-foreground">{r.designation}</td>
                    <td className="px-3 py-1 text-right num">{r.required}</td>
                    <td className="px-3 py-1 text-right num">{r.current}</td>
                    <td className={`px-3 py-1 text-right num ${r.gap > 0 ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{r.gap}</td>
                    <td className="px-3 py-1 text-right num text-muted-foreground">{r.required > 0 ? `${Math.round((r.current / r.required) * 100)}%` : "—"}</td>
                  </tr>
                )),
              ]
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Branches() {
  const { filters } = useApp();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get(`/hr/branches?${qs({ brand: filters.brand, basis: filters.basis })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ branches: [], can_view_market: false }));
  }, [filters.brand, filters.basis]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading branches…</div>;

  if (sel) return <BranchCard name={sel} onBack={() => setSel(null)} canMarket={data.can_view_market} />;

  if (data.branches.length === 0)
    return (
      <div className="border bg-card rounded-md p-16 text-center max-w-xl mx-auto mt-10" data-testid="branches-empty">
        <UploadCloud className="mx-auto text-muted-foreground" size={40} />
        <h2 className="font-heading text-lg font-semibold mt-4">No manpower data yet</h2>
        <p className="text-sm text-muted-foreground mt-1">Upload the HR master sheet (Manpower Dashboard .xlsx) on the Uploads screen to light up branch report cards.</p>
      </div>
    );

  const list = data.branches.filter((b) => !search || b.display.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-5" data-testid="branches-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Branch Report Cards</h1>
        <span className="text-xs text-muted-foreground">{data.branches.length} branches · click a branch for its full report card</span>
        <Input placeholder="Search branch…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-48 text-sm ml-auto" data-testid="branch-search-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {list.map((b) => (
          <button
            key={b.branch}
            onClick={() => setSel(b.branch)}
            className="border bg-card rounded-md p-4 text-left hover:shadow-md hover:-translate-y-[1px] transition-transform duration-200"
            data-testid={`branch-card-${b.branch}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <div className="font-heading font-bold">{b.display}</div>
              {b.share != null && <div className="num text-lg font-extrabold" style={{ color: brandColor(data.brand) }}>{pct(b.share)}</div>}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
              {b.share != null
                ? `${data.brand} share (MTD) · ${b.dealer_code} · TIV ${fmt(b.tiv)}`
                : data.can_view_market
                  ? (b.dealer_code ? `${b.dealer_code}` : "No dealer linked")
                  : "Manpower report card"}
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Manpower {b.current}/{b.required}</span>
                <span className={`font-semibold ${b.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{b.gap > 0 ? `Gap ${b.gap}` : "Full"}</span>
              </div>
              <FillBar availability={b.availability} />
              <div className={`text-[10px] font-semibold ${availTone(b.availability)}`}>{b.availability != null ? `${b.availability}% filled` : "—"}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
